import React from "react";

import { useNavigation } from "@refinedev/core";
import { CalendarCheck, Loader2, PlusIcon } from "lucide-react";

import { DeleteButton } from "@/components/refine-ui/buttons/delete";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCostSubscriptions } from "@/pages/tina/cost-subscriptions/hooks/use-cost-subscriptions";
import {
  localIsoDate,
  perDayThisMonth,
} from "@/pages/tina/cost-subscriptions/lib/cost-subscription-format";
import type { CostSubscriptionRead } from "@/types/tina";

const RESOURCE = "tina-cost-subscriptions";

const EndPlanButton = ({
  subscription,
  onEnd,
}: {
  subscription: CostSubscriptionRead;
  onEnd: (id: string, date: string) => Promise<boolean>;
}) => {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const today = localIsoDate(new Date());

  const confirm = async () => {
    setBusy(true);
    await onEnd(subscription.id, today);
    setBusy(false);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" type="button">
          <CalendarCheck className="h-4 w-4" />
          End plan
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto" align="end">
        <div className="flex flex-col gap-2">
          <p className="text-sm">
            End "{subscription.name}" today ({today})?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={confirm}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              End plan
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const CostSubscriptionsList = () => {
  const { create, edit } = useNavigation();
  const { subscriptions, isLoading, isError, refetch, endPlan } =
    useCostSubscriptions();
  const now = new Date();
  const today = localIsoDate(now);

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Subscriptions</h1>
        <Button onClick={() => create(RESOURCE)}>
          <PlusIcon className="h-4 w-4 mr-1" />
          New subscription
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Flat monthly plans (Browserbase, RapidAPI…). Each one is spread evenly
        over its days in the spend-over-time chart, from its start date until
        its end date.
      </p>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead className="text-right">$/month</TableHead>
              <TableHead>Start</TableHead>
              <TableHead>End</TableHead>
              <TableHead className="text-right">$/day this month</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  role="alert"
                  className="h-24 text-center text-destructive"
                >
                  Could not load the subscriptions.{" "}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => void refetch()}
                  >
                    Retry
                  </button>
                </TableCell>
              </TableRow>
            ) : subscriptions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="h-24 text-center text-muted-foreground"
                >
                  No subscriptions yet.
                </TableCell>
              </TableRow>
            ) : (
              subscriptions.map((row) => (
                <TableRow
                  key={row.id}
                  onDoubleClick={() => edit(RESOURCE, row.id)}
                >
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell>{row.vendor}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.monthly_usd}
                  </TableCell>
                  <TableCell>{row.starts_on}</TableCell>
                  <TableCell>
                    {row.ends_on ?? <Badge variant="secondary">active</Badge>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {perDayThisMonth(
                      row.monthly_usd,
                      row.starts_on,
                      row.ends_on,
                      now,
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {row.ends_on === null && row.starts_on <= today && (
                        <EndPlanButton subscription={row} onEnd={endPlan} />
                      )}
                      <EditButton
                        size="sm"
                        hideText
                        recordItemId={row.id}
                        resource={RESOURCE}
                      />
                      <DeleteButton
                        resource={RESOURCE}
                        id={row.id}
                        onSuccess={() => {
                          void refetch();
                        }}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
